/**
 * useFieldTypes.ts
 * Hook that exposes the catalog field types to the UI.
 *
 * The field-type catalog is owned entirely by the backend DB and read via
 * GET /field-types. The frontend keeps no field-type data of its own — this
 * hook just fetches, groups and exposes whatever the backend returns.
 *
 * Used by the new-type wizard, the field-settings panel and the editor's
 * add-field dropdown so all three stay in sync from one source.
 */
import { useEffect, useMemo, useState } from 'react';
import type { FieldTypeDef } from '../schemas/catalog/fieldType';
import { getFieldTypes } from '../services/catalog/fieldType';

export interface UseFieldTypes {
  /** Every type returned by the backend (incl. hidden ones, for meta lookups). */
  all: FieldTypeDef[];
  /** Creatable types — what every picker iterates. */
  picker: FieldTypeDef[];
  /** Wizard "default" group. */
  friendly: FieldTypeDef[];
  /** Wizard "More types…" group. */
  advanced: FieldTypeDef[];
  /** Label/icon for any type value (defensive default for an unknown value). */
  meta: (type: string) => { label: string; friendlyLabel: string; icon: string };
  /** True while the catalog is being fetched. */
  loading: boolean;
  /** True if the fetch failed. */
  error: boolean;
}

export function useFieldTypes(): UseFieldTypes {
  const [types, setTypes] = useState<FieldTypeDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getFieldTypes()
      .then((items) => { if (alive) { setTypes(items); setError(false); } })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return useMemo(() => {
    const byType: Record<string, FieldTypeDef> = {};
    types.forEach((t) => { byType[t.type] = t; });
    const picker = types.filter((t) => t.inPicker);
    return {
      all: types,
      picker,
      friendly: picker.filter((t) => t.tier === 'friendly'),
      advanced: picker.filter((t) => t.tier === 'advanced'),
      meta: (type: string) => byType[type] ?? { label: type, friendlyLabel: type, icon: 'list' },
      loading,
      error,
    };
  }, [types, loading, error]);
}
